// src/components/Header.tsx
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthProvider";

import logoBlue from "../assets/kailospay/logo-blue.png";

const NEXT_AFTER_LOGIN = "/start";

export default function Header() {
  const nav = useNavigate();
  const { user, logout } = useAuth();

  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const isAdmin = !!user?.is_admin;

  // ✅ 결제 버튼 라벨
  const mainActionLabel = user ? "결제하러가기" : "결제등록";

  const handleMainAction = () => {
    if (!user) nav(`/login?next=${encodeURIComponent(NEXT_AFTER_LOGIN)}`);
    else nav("/pay");
  };

  const myPageLabel = isAdmin ? "관리자 페이지" : "마이페이지";

  const handleMyPage = () => {
    if (isAdmin) nav("/admin");
    else nav("/my");
  };

  const loginNext = encodeURIComponent(
    window.location.pathname + window.location.search + window.location.hash
  );

  // ✅ 공통: 오렌지 알약(그라데이션+쉐도우)
  const pillPrimary =
    "inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold " +
    "text-white bg-gradient-to-r from-[#ff6a00] to-[#ffb074] " +
    "shadow-[0_10px_28px_rgba(255,106,0,.30)] " +
    "hover:brightness-110 active:translate-y-px transition " +
    "focus:outline-none focus:ring-2 focus:ring-brand-500/30";

  // ✅ 보조(아웃라인 + 은은한 오렌지 쉐도우)
  const pillSoft =
    "inline-flex items-center justify-center rounded-full px-5 py-2 text-sm font-semibold " +
    "text-[#ea580c] border border-[#ea580c]/45 bg-white/75 backdrop-blur " +
    "shadow-[0_10px_26px_rgba(234,88,12,.16)] " +
    "hover:bg-white hover:border-[#ea580c]/60 active:translate-y-px transition " +
    "focus:outline-none focus:ring-2 focus:ring-brand-500/25";

  // ✅ 결제하러가기(또는 결제등록) 버튼을 더 “눈에 띄게”
  const pillMainAction =
    "inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-extrabold " +
    "text-white bg-gradient-to-r from-[#ff6a00] via-[#ff8a3d] to-[#ffb074] " +
    "shadow-[0_14px_34px_rgba(30,94,255,.36)] " +
    "ring-1 ring-brand-200/40 " +
    "hover:brightness-110 hover:scale-[1.02] active:translate-y-px transition " +
    "focus:outline-none focus:ring-2 focus:ring-brand-500/35";

  // ─────────────────────────────────────────────
  // ✅ 모바일 메뉴 닫기 UX
  // ─────────────────────────────────────────────
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    if (open) window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (!open) return;
      const el = panelRef.current;
      if (!el) return;
      if (el.contains(e.target as Node)) return;
      setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  // ✅ 라우트 이동 시 모바일 메뉴 자동 닫기(UX)
  useEffect(() => {
    if (open) setOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/80 pt-[calc(env(safe-area-inset-top)+12px)] md:pt-0 backdrop-blur pointer-events-auto">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4">
        {/* ✅ 로고(이미지) */}
        <Link to="/" className="inline-flex items-center">
          <img
            src={logoBlue}
            alt="주류페이"
            className="h-10 w-auto"
            draggable={false}
          />
        </Link>

        {/* Desktop 메뉴 */}
        <nav className="hidden items-center gap-5 text-sm md:flex z-[60] pointer-events-auto">
          <button
            type="button"
            onClick={() => nav("/support")}
            className="text-slate-700 hover:text-slate-900"
          >
            고객센터
          </button>

          <button
            type="button"
            onClick={() => nav("/guide")}
            className="text-slate-700 hover:text-slate-900"
          >
            이용안내
          </button>

          <button type="button" onClick={handleMainAction} className={pillMainAction}>
            {mainActionLabel}
          </button>

          <button
            type="button"
            onClick={handleMyPage}
            className="text-slate-700 hover:text-slate-900"
          >
            {myPageLabel}
          </button>

          {user ? (
            <>
              <span className="ml-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1.5 text-xs text-slate-700">
                {user.name}님
              </span>
              <button type="button" onClick={logout} className={pillSoft}>
                로그아웃
              </button>
            </>
          ) : (
            <Link to={`/login?next=${loginNext}`} className={pillPrimary}>
              로그인
            </Link>
          )}
        </nav>

        {/* Mobile hamburger / close */}
        <button
          type="button"
          aria-label={open ? "메뉴 닫기" : "메뉴 열기"}
          aria-expanded={open}
          className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 bg-white/70 backdrop-blur"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? (
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
              <path strokeWidth="2" strokeLinecap="round" d="M18 6L6 18M6 6l12 12" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
              <path strokeWidth="2" strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile dropdown + overlay */}
      {open && (
        <div className="md:hidden fixed inset-0 z-[100]">
          <button
            type="button"
            aria-label="메뉴 닫기"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-black/20"
          />

          <div
            ref={panelRef}
            className="absolute right-4 mt-2 w-56 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
            style={{ top: "calc(64px + env(safe-area-inset-top) + 12px)" }}
            role="menu"
          >
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
              <div className="text-xs font-semibold text-slate-700">메뉴</div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white hover:bg-slate-50 active:bg-slate-100"
                aria-label="닫기"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
                  <path strokeWidth="2" strokeLinecap="round" d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="flex flex-col p-2 text-sm">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  nav("/support");
                }}
                className="rounded-lg px-3 py-2 text-left hover:bg-slate-50"
                role="menuitem"
              >
                고객센터
              </button>

              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  nav("/guide");
                }}
                className="rounded-lg px-3 py-2 text-left hover:bg-slate-50"
                role="menuitem"
              >
                이용안내
              </button>

              <div className="my-1 h-px bg-slate-100" />

              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  handleMainAction();
                }}
                className="mx-2 mt-1 inline-flex items-center justify-center rounded-full px-4 py-2.5 text-sm font-extrabold text-white bg-gradient-to-r from-[#ff6a00] via-[#ff8a3d] to-[#ffb074] shadow-[0_12px_26px_rgba(30,94,255,.34)] ring-1 ring-brand-200/40 hover:brightness-110 active:translate-y-px transition"
                role="menuitem"
              >
                {mainActionLabel}
              </button>

              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  handleMyPage();
                }}
                className="mt-1 rounded-lg px-3 py-2 text-left hover:bg-slate-50"
                role="menuitem"
              >
                {myPageLabel}
              </button>

              <div className="my-1 h-px bg-slate-100" />

              {user ? (
                <>
                  <div className="rounded-lg px-3 py-2 text-slate-700">{user.name}님</div>
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      logout();
                    }}
                  className="mx-2 mb-2 mt-1 rounded-full px-4 py-2 text-sm font-semibold text-[#ea580c] border border-[#ea580c]/45 bg-white/80 shadow-[0_10px_22px_rgba(234,88,12,.16)] hover:bg-white hover:border-[#ea580c]/60 active:translate-y-px transition"
                  role="menuitem"
                  >
                    로그아웃
                  </button>
                </>
              ) : (
                <Link
                  to={`/login?next=${loginNext}`}
                  onClick={() => setOpen(false)}
                  className="mx-2 mb-2 mt-1 inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-semibold text-white bg-gradient-to-r from-[#ff6a00] to-[#ffb074] shadow-[0_10px_22px_rgba(255,106,0,.28)] hover:brightness-110 active:translate-y-px transition"
                  role="menuitem"
                >
                  로그인
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
